import { DesignData, ElementAddress, MediaText, TextStyle, TextWrapping } from 'editor/src/store/design/types';
import { TextAlign } from 'editor/src/store/fonts/types';
import isFontFileLoaded from 'editor/src/store/fonts/utils/isFontFileLoaded';
import type { RootState } from 'editor/src/store/index';
import shouldDigitizeSpread from 'editor/src/store/utils/shouldDigitizeSpread';

import CMYK from 'editor/src/util/color/cmykUtils';
import { getModule, loadLittleCMSModule } from 'editor/src/util/color/cmykUtils/littleCMSModule';
import { parseColorToRGBA } from 'editor/src/util/color/colorUtils';
import HSVA from 'editor/src/util/color/hsvUtils';
import RGBA from 'editor/src/util/color/rgbaUtils';

import { extractTextProperties } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

export const getTextStyle = (designData: DesignData | undefined, styleId: string | undefined) =>
  styleId ? designData?.textStyles?.find((style) => style.id === styleId) : undefined;

export interface TextStyleMember {
  element: MediaText;
  address: ElementAddress;
}

/** Every text in the design that follows the style, across all spreads and pages. */
export function getTextStyleMembers(designData: DesignData | undefined, styleId: string): TextStyleMember[] {
  const members: TextStyleMember[] = [];
  designData?.spreads.forEach((spread, spreadIndex) =>
    spread.pages.forEach((page, pageIndex) =>
      page.groups.media?.forEach((element, elementIndex) => {
        if (element.type === 'text' && element.extra.textStyleId === styleId) {
          members.push({ element, address: { spreadIndex, pageIndex, elementIndex } });
        }
      }),
    ),
  );
  return members;
}

export type TextStyleValues = Omit<TextStyle, 'id' | 'name'>;

// How a text without the property renders (textElementDataToFabricProps).
const getTextAlign = (element: MediaText) => element.extra.textAlign ?? TextAlign.left;
const getLetterSpacing = (element: MediaText) => element.extra.letterSpacing ?? 0;

/**
 * Reads the look of a text as a style, e.g. to create a style from the selected text. Alignment,
 * underline, strikethrough and letter spacing are read as the text renders them, so the new
 * style governs them too.
 */
export const getTextStyleValuesFromElement = (element: MediaText): TextStyleValues => ({
  fontFamily: element.extra.fontFamily,
  fontSize: element.extra.fontSize,
  color: element.fill,
  lineHeight: element.extra.lineHeight,
  textAlign: getTextAlign(element),
  underline: !!element.extra.underline,
  linethrough: !!element.extra.linethrough,
  letterSpacing: getLetterSpacing(element),
  // unset on the text is unset on the style, so a new style does not start governing spacing
  paragraphSpacing: element.extra.paragraphSpacing,
  sectionSpacing: element.extra.sectionSpacing,
});

/** How a style's colour is written onto one of its texts: see getTextStyleFillMode. */
export type TextStyleFillMode = 'asIs' | 'ink' | 'plain';

/**
 * A style's texts sit on every spread, and under the CMYK lock its colour is picked in RGB on an
 * embroidered spread, whose thread colours are matched by their hex. So under the lock a text on a
 * printed spread gets the colour as ink and one on an embroidered spread its plain hex; without the
 * lock every text takes the colour as the style has it, whatever the design's colour space.
 */
export function getTextStyleFillMode(
  designData: DesignData | undefined,
  cmykOnly: boolean,
  spreadIndex: number,
): TextStyleFillMode {
  if (!cmykOnly) {
    return 'asIs';
  }
  const spread = designData?.spreads[spreadIndex];
  return spread && shouldDigitizeSpread(spread) ? 'plain' : 'ink';
}

// The selection is on the current spread.
export const getSelectionFillMode = (state: RootState): TextStyleFillMode =>
  state.hostSettings.cmykOnly
    ? getTextStyleFillMode(state.design.designData, true, state.editor.currentSpreadIndex)
    : 'asIs';

/** How the text `uuid` on `spreadIndex` gets the style's fill (see getTextStyleMemberFill). */
export function getTextStyleFillWrite(
  designData: DesignData | undefined,
  cmykOnly: boolean,
  style: TextStyle,
  spreadIndex: number,
  uuid: number,
): TextStyleFillWrite {
  const mode = getTextStyleFillMode(designData, cmykOnly, spreadIndex);
  return {
    mode,
    printedFill: mode === 'ink' ? getPrintedTextStyleFill(designData, cmykOnly, style, uuid) : undefined,
  };
}

export const ALL_TEXT_STYLE_FILL_MODES: readonly TextStyleFillMode[] = ['asIs', 'ink', 'plain'];

// Style colours are few, and the modified check converts them for every member on every selector run.
const INK_CACHE_SIZE = 64;
const inkCache = new Map<string, string | undefined>();

/**
 * The colour as ink, or undefined when it can't be stored as one: ink has no alpha, so a see-through
 * or empty colour (an outline-only text) would print solid, and before the print profile loads the
 * conversion is only the rough fallback.
 */
function getInk(color: string): string | undefined {
  if (!getModule()) {
    return undefined;
  }
  if (!inkCache.has(color)) {
    if (inkCache.size >= INK_CACHE_SIZE) {
      inkCache.clear();
    }
    const rgba = RGBA.fromString(color) ?? HSVA.fromString(color)?.toRGB();
    inkCache.set(color, rgba && rgba.a >= 1 ? CMYK.fromRGB(rgba).toString() : undefined);
  }
  return inkCache.get(color);
}

export function getTextStyleFill(color: string, mode: TextStyleFillMode): string {
  const isInk = !!CMYK.fromString(color);
  if (mode === 'ink' && !isInk) {
    return getInk(color) ?? color;
  }
  if (mode === 'plain' && isInk) {
    return parseColorToRGBA(color).toHex();
  }
  return color;
}

// Whether a fill is the style's colour in a form getTextStyleFill writes in one of `modes`. A thread
// colour is matched by its exact hex, so a neighbouring thread that rounds to the same ink differs.
export function hasTextStyleColor(
  fill: string | undefined,
  color: string,
  modes: readonly TextStyleFillMode[],
): boolean {
  // an embroidered text is sewn from its hex, so an ink there is never the style's thread colour
  if (fill && modes.length === 1 && modes[0] === 'plain' && CMYK.fromString(fill)) {
    return false;
  }
  // Until the print profile loads, an ink can't be compared with a hex colour. It counts as the
  // style's, so a style write leaves it and it doesn't read as Modified.
  if (fill && modes.includes('ink') && !getModule() && CMYK.fromString(fill) && !CMYK.fromString(color)) {
    return true;
  }
  const normalizedFill = normalizeColor(fill);
  return (
    normalizedFill === normalizeColor(color) ||
    (!!fill &&
      modes.some((mode) => mode !== 'asIs' && normalizedFill === normalizeColor(getTextStyleFill(color, mode))))
  );
}

export interface TextStyleFillWrite {
  mode: TextStyleFillMode;
  // the fill the style's other printed texts already have, which a printed text given the colour takes
  printedFill?: string;
}

/**
 * The fill a style write gives a text, or undefined to leave it. Without the CMYK lock the colour
 * is written as the style has it. Under it a text already in the style's colour keeps its form of
 * it, so a write re-colours nothing that did not change; any other text gets the colour as every
 * style write does, a printed one in the form the style's other printed texts have, so hex and ink
 * don't mix.
 */
function getTextStyleMemberFill(fill: string, color: string, write: TextStyleFillWrite): string | undefined {
  const { mode, printedFill } = write;
  if (mode === 'asIs') {
    return color;
  }
  if (hasTextStyleColor(fill, color, [mode])) {
    return undefined;
  }
  if (mode === 'ink' && printedFill !== undefined) {
    return printedFill;
  }
  return getTextStyleFill(color, mode);
}

/** A printed text of the style already in its colour, other than `except`: the form new ones copy. */
export function getPrintedTextStyleFill(
  designData: DesignData | undefined,
  cmykOnly: boolean,
  style: TextStyle,
  except: number,
): string | undefined {
  // before the print profile loads, an ink only counts as the style's colour so it is left alone
  if (style.color === undefined || !getModule()) {
    return undefined;
  }
  const { color } = style;
  return getTextStyleMembers(designData, style.id).find(
    ({ element, address }) =>
      element.uuid !== except &&
      getTextStyleFillMode(designData, cmykOnly, address.spreadIndex) === 'ink' &&
      hasTextStyleColor(element.fill, color, ['ink']),
  )?.element.fill;
}

/**
 * Under the CMYK lock a style write waits for the print profile, which tells a text's ink apart from
 * the style's hex. A failed load is reported where it starts; the write then leaves inks as they are.
 */
export async function loadTextStyleColorProfile(): Promise<void> {
  if (!getModule()) {
    await loadLittleCMSModule().catch(() => undefined);
  }
}

/** The optional properties a style writes onto its texts' `extra` when it sets them. */
const OPTIONAL_EXTRA_KEYS = [
  'lineHeight',
  'textAlign',
  'underline',
  'linethrough',
  'letterSpacing',
  'paragraphSpacing',
  'sectionSpacing',
] as const;

/**
 * The update that makes a text follow the style: the style's values written
 * onto the text, the reference set, and the box re-measured for the new font
 * and size the same way a font edit from the toolbar re-measures it.
 */
export function buildTextStyleElementUpdate(
  element: MediaText,
  style: TextStyle,
  fillWrite: TextStyleFillWrite,
): Partial<MediaText> {
  const update: Partial<MediaText> = {
    extra: {
      ...element.extra,
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      ...Object.fromEntries(
        OPTIONAL_EXTRA_KEYS.filter((key) => style[key] !== undefined).map((key) => [key, style[key]]),
      ),
      textStyleId: style.id,
    },
  };
  const fill = style.color === undefined ? undefined : getTextStyleMemberFill(element.fill, style.color, fillWrite);
  if (fill !== undefined) {
    update.fill = fill;
  }

  // A new font needs a freshly rendered server path, as with the font picker.
  if (style.fontFamily !== element.extra.fontFamily) {
    update.path = undefined;
  }

  Object.assign(update, measureTextStyleElement({ ...element, ...update } as MediaText));
  return update;
}

/**
 * The box of a text re-measured for its font and size, the same way a font edit from the
 * toolbar re-measures it; empty when the text is not re-measured. Same exclusions as
 * applyFontStylesToSelectionOperation: a fitted text sizes itself to its box, and a
 * protected font that is not loaded cannot be measured.
 */
export function measureTextStyleElement(element: MediaText): Partial<MediaText> {
  const { fontFamily, wrapping } = element.extra;
  const isProtectedCustomFont = fontFamily.startsWith('custom-') && !isFontFileLoaded(fontFamily);
  if (isProtectedCustomFont || wrapping === TextWrapping.Fit || wrapping === TextWrapping.FitOneLine) {
    return {};
  }
  const { width, height, sx, sy, x, y } = extractTextProperties(element, undefined);
  return { width, height, sx, sy, x, y };
}

// Sizes read back from fabric carry float noise (28.000000000000004).
const roundSize = (size: number) => Math.round(size * 100) / 100;

/**
 * One spelling per colour, so '#FF0000', '#f00' and 'rgb(255, 0, 0)' compare equal.
 * Alpha is kept; anything unparseable compares as written.
 */
export function normalizeColor(color: string | undefined): string | undefined {
  if (!color) {
    return color;
  }
  const value = color.trim().toLowerCase();
  const shortHex = value.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
  if (shortHex) {
    return `#${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}${shortHex[3]}${shortHex[3]}`;
  }
  const rgb = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (rgb) {
    const hex = `#${rgb
      .slice(1, 4)
      .map((v) => Math.min(255, Number(v)).toString(16).padStart(2, '0'))
      .join('')}`;
    return rgb[4] === undefined || Number(rgb[4]) === 1 ? hex : `${hex}/${Number(rgb[4])}`;
  }
  return value;
}

/**
 * Whether a text that follows the style no longer looks like it: a property the style
 * governs differs from the style's value. Font and size are always the style's; line
 * height, colour, alignment, underline, strikethrough and letter spacing only when the style
 * sets them. Derived, never stored: editing a styled
 * text from the toolbar keeps it in its style (as in Docs/Word), it is just "modified"
 * until the author updates the style from it or resets it to the style.
 */
export function isTextStyleModified(
  style: TextStyle | undefined,
  element: MediaText,
  fillMode: TextStyleFillMode,
): boolean {
  if (!style) {
    return false;
  }
  return (
    element.extra.fontFamily !== style.fontFamily ||
    roundSize(element.extra.fontSize) !== roundSize(style.fontSize) ||
    (style.lineHeight !== undefined && element.extra.lineHeight !== style.lineHeight) ||
    (style.color !== undefined && !hasTextStyleColor(element.fill, style.color, [fillMode])) ||
    (style.textAlign !== undefined && getTextAlign(element) !== style.textAlign) ||
    (style.underline !== undefined && !!element.extra.underline !== style.underline) ||
    (style.linethrough !== undefined && !!element.extra.linethrough !== style.linethrough) ||
    (style.letterSpacing !== undefined && getLetterSpacing(element) !== style.letterSpacing) ||
    (style.paragraphSpacing !== undefined && element.extra.paragraphSpacing !== style.paragraphSpacing) ||
    (style.sectionSpacing !== undefined && element.extra.sectionSpacing !== style.sectionSpacing)
  );
}

/**
 * The style update that makes the style match a text: only what the style governs, so a
 * style without e.g. a colour or line height does not start governing one. A text that has the
 * style's colour in its spread's form leaves the colour as the style has it, so the other texts
 * keep theirs. An embroidered text gives its thread hex, never an ink, whose soft-proof hex would
 * land on the style's other embroidered texts.
 */
export function getTextStyleUpdateFromElement(
  style: TextStyle,
  element: MediaText,
  fillMode: TextStyleFillMode,
): Partial<TextStyleValues> {
  const values = getTextStyleValuesFromElement(element);
  if (fillMode === 'plain' && values.color !== undefined) {
    values.color = getTextStyleFill(values.color, 'plain');
  }
  const keepsColor =
    fillMode !== 'asIs' &&
    style.color !== undefined &&
    (hasTextStyleColor(element.fill, style.color, [fillMode]) ||
      (fillMode === 'plain' && hasTextStyleColor(element.fill, style.color, ['ink'])));
  const governed = (['color', ...OPTIONAL_EXTRA_KEYS] as const).filter(
    (key) => style[key] !== undefined && values[key] !== undefined && !(key === 'color' && keepsColor),
  );
  return {
    fontFamily: values.fontFamily,
    fontSize: values.fontSize,
    ...Object.fromEntries(governed.map((key) => [key, values[key]])),
  };
}

/**
 * A text the current user may not edit directly: personalization-locked by the template, in an
 * editor that honours locks (the buyer's). Style edits skip it, so a style cannot restyle what
 * the template author locked.
 */
export const isTextStyleProtected = (state: RootState, element: MediaText) =>
  !!element.personalizationLocked && !state.hostSettings?.ignorePersonalizationLock;
